import { filterInData } from '@repo/utils/filter-in-data';
import { cache } from 'react';
// import { overwriteGamesCategories, overwriteGamesProperties } from '@/lib/utils/overwrite-games-properties';
import type { GameCategoryResponseType } from './client';

// Define a generic type for game objects based on inference
type InferredGame = Record<string, any>;

// Use the generic game type in the response structure
export interface PaginatedGamesResponse {
  games: InferredGame[];
  total: number;
  page: number;
  limit: number;
  hasNextPage: boolean;
}

// Define the interface for the function parameters
interface GetCasinoGamesListParams {
  types: string[];
  page?: number;
  limit?: number;
  providerId?: string | null;
  tenantMachineName: string | null;
}

export const getCasinoGamesTypes = cache(async (tenantMachineName?: string | null) => {
  if (!tenantMachineName) {
    return [];
  }

  const importedModule = await import(`@/data/casino-games/game-types-${tenantMachineName}.json`);
  const { default: gameTypes } = importedModule;
  return gameTypes as unknown as GameCategoryResponseType;
});

export const getCasinoGamesProviders = cache(async (tenantMachineName?: string | null) => {
  if (!tenantMachineName) {
    return [];
  }

  const importedModule = await import(`@/data/casino-games/games-providers-${tenantMachineName}.json`);
  const { default: gameProviders } = importedModule;
  return gameProviders as unknown as GameCategoryResponseType;
});

/**
 * Get casino games by type
 *
 * @param types - The types of games to get
 * @param page - The page number
 * @param limit - The number of games per page
 * @param providerId - The provider ID
 * @returns An array of games
 */
export const getCasinoGamesList = cache(
  async ({
    types,
    page = 1, // Default to page 1
    limit = 50, // Default limit
    providerId = null, // Default to null
    tenantMachineName,
  }: GetCasinoGamesListParams): Promise<PaginatedGamesResponse> => {
    if (!tenantMachineName) {
      return {
        games: [],
        total: 0,
        page,
        limit,
        hasNextPage: false,
      };
    }

    const importedModule = await import(`@/data/casino-games/games-list-${tenantMachineName}.json`);
    const { default: gamesList } = importedModule;

    // 1. Filter by type ONLY if the types array is not empty
    let gamesToPaginate: InferredGame[];
    if (types?.length) {
      gamesToPaginate = gamesList.filter(
        (game: InferredGame) =>
          game.gameType && Array.isArray(game.gameType) && types.some((type) => game.gameType!.includes(type))
      );
    } else {
      gamesToPaginate = gamesList;
    }

    // 2. Filter by providerId if provided
    if (providerId) {
      gamesToPaginate = gamesToPaginate.filter((game) => game.provider.toLowerCase() === providerId.toLowerCase());
    }

    // 3. Apply pagination to the (potentially filtered) list
    const total = gamesToPaginate.length;
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedGames = gamesToPaginate.slice(startIndex, endIndex);
    const hasNextPage = endIndex < total;

    return {
      games: paginatedGames,
      total,
      page,
      limit,
      hasNextPage,
    };
  }
);

/**
 * Get casino games by search term
 *
 * @param search - The search term
 * @param tenantMachineName - The tenant machine name
 * @returns An array of games
 */
export const getCasinoGamesByKey = cache(
  async (search?: string, tenantMachineName?: string | null): Promise<InferredGame[]> => {
    if (!tenantMachineName) {
      return [];
    }

    const importedModule = await import(`@/data/casino-games/games-list-${tenantMachineName}.json`);
    const { default: gamesList } = importedModule;

    // 1. Filter by search term
    const searchFiltered = filterInData(
      gamesList as InferredGame[],
      ['name', 'provider'],
      search || undefined
    ) as InferredGame[];

    return searchFiltered;
  }
);

/**
 * Get casino game details
 *
 * @param gameId - The game ID
 * @param tenantMachineName - The tenant machine name
 *
 * @returns The game details
 */
export const getCasinoGameDetails = cache(async (gameId: number, tenantMachineName: string) => {
  if (!tenantMachineName) {
    return null;
  }

  const importedModule = await import(`@/data/casino-games/games-list-${tenantMachineName}.json`);
  const { default: gamesList } = importedModule;

  return gamesList.find((game: InferredGame) => game.id === gameId);
});
